import { DensityType } from '@/components/DisplayManagement/types'
import { useTableDensity } from '@/components/DisplayManagement/useTableDensity'
import { BaselineTurntableTheme } from '@/utils/table-theme'
import { CustomTheme } from '@flatfile/turntable'
import { createContext, useCallback, useContext, useState } from 'react'

const ROW_HEIGHTS = {
  default: 30,
  comfortable: 35,
  compact: 25,
} as const

interface SheetThemeContextType {
  density: DensityType
  theme: CustomTheme
  updateDensity: (newDensity: DensityType) => void
}

const SheetThemeContext = createContext<SheetThemeContextType | undefined>(
  undefined
)

export function SheetThemeProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { density, setDensity } = useTableDensity()
  const [theme, setTheme] = useState<CustomTheme & { rowHeight: number }>({
    ...BaselineTurntableTheme,
    rowHeight: ROW_HEIGHTS[density],
  })

  const updateDensity = useCallback(
    (newDensity: DensityType) => {
      setDensity(newDensity)
      setTheme((currentTheme) => ({
        ...currentTheme,
        rowHeight: ROW_HEIGHTS[newDensity],
      }))
    },
    [setDensity]
  )

  return (
    <SheetThemeContext.Provider value={{ density, theme, updateDensity }}>
      {children}
    </SheetThemeContext.Provider>
  )
}

export function useSheetTheme() {
  const context = useContext(SheetThemeContext)
  if (!context) {
    throw new Error('useSheetTheme must be used within a SheetThemeProvider')
  }
  return context
}
